import * as React from 'react';
import { SelectItem } from 'vet-bones/utils/select';

import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  ModelConceptClass,
  ModelConceptClassHierarchy,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/data';
import { cleanupModelConceptHierarchyMappings } from 'src/containers/ProjectDiagram/utils/mapping/cleanupModelConceptHierarchyMappings';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/addModelConceptHierarchy';
import { getModelConceptHierarchyKey } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchy';
import { removeModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/removeModelConceptHierarchy';
import { getProjectModelIdsForIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';

export const useAddHierarchyForClass = ({
  nodeData,
  type,
}: {
  nodeData: ModelConceptClass | null;
  type: ModelConceptClassHierarchy;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !nodeData) {
        return;
      }

      const { text, value, isNewlyCreated } = item;
      if (isNewlyCreated && !text) {
        return;
      }

      const [fromIri, toIri] =
        type === ModelConceptClassHierarchy.CHILDREN
          ? [value, nodeData.value]
          : [nodeData.value, value];

      const commitFn = (model: Model) => {
        if (isNewlyCreated) {
          addModelConceptClass({
            iri: value,
            label: text,
            projectModelIds: getProjectModelIdsForIri(nodeData.value, model),
          })(model);
        }
        addModelConceptHierarchy({ fromIri, toIri })(model);
      };

      commitDiagramModel(diagram, commitFn, {
        layout: DiagramModelCommitLayout.PARTIAL,
      });
    },
    [diagram, type, nodeData]
  );
};

export const useRemoveHierarchyForClass = ({
  nodeData,
  type,
}: {
  nodeData: ModelConceptClass | null;
  type: ModelConceptClassHierarchy;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !nodeData) {
        return;
      }

      const [fromIri, toIri] =
        type === ModelConceptClassHierarchy.CHILDREN
          ? [item.value, nodeData.value]
          : [nodeData.value, item.value];

      // Resolve the hierarchy link from the model so removal works headless.
      const key = getModelConceptHierarchyKey({ fromIri, toIri });
      const linkData = diagram.model.findLinkDataForKey(key);

      if (linkData && isModelConceptHierarchy(linkData)) {
        commitDiagramModel(
          diagram,
          (model) => {
            addDeletedData([linkData])(model);
            removeModelConceptHierarchy(linkData)(model);
            cleanupModelConceptHierarchyMappings(model);
          },
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      }
    },
    [diagram, nodeData, type]
  );
};
